import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import { useSetState, useDebounce } from '@campaignhub/react-hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes } from '@fortawesome/pro-light-svg-icons'
import useProvidersV4 from '@hooks/useProvidersV4'

import {
  Box, FormField, DashboardModule, Tag, Text,
} from '@campaignhub/suit-theme'

import InfiniteLinkingTags from '@components/InfiniteLinkingTags'

import { isValidEmail } from '@functions/validations'

const defaultState = {
  calendarInviteEmail: '',
  proofingEmail: '',
  searchFilter: '',
}

const Preferences = (props) => {
  const { clientForm, linkedBlacklistedProviders = [], callbacks } = props
  const { updateLinkedBlacklistedProviders } = callbacks || {}

  const [state, setState] = useSetState(defaultState)
  const { calendarInviteEmail, proofingEmail, searchFilter } = state

  const debouncedSearchFilter = useDebounce(searchFilter, 300)

  const {
    entityState,
    entityState: {
      blacklisted_providers,
      calendar_invite_emails,
      proofing_emails,
    },
    setEntityState,
  } = clientForm

  const { filteredProvidersV4: filteredProviders, loading, hasMore, callbacks: { loadNextPage, loadInitialPage } } = useProvidersV4({ searchFilter: debouncedSearchFilter })

  useEffect(() => {
    loadInitialPage()
  }, [debouncedSearchFilter])

  const toggleProvider = (provider, type) => {
    let newList = []
    if (type === 'add'){
      newList = [...linkedBlacklistedProviders, provider]
    } else {
      newList = linkedBlacklistedProviders.filter(x => x.reference !== provider.reference)
    }
    updateLinkedBlacklistedProviders?.(newList)
    setEntityState({ blacklisted_providers: newList.map(p => p.reference) })
  }

  const handleAddEmails = (email, type) => {
    if (isValidEmail(email)){
      if (type === 'calendarInvite'){
        const updateInviteEmails = [...new Set([...calendar_invite_emails, email])]
        setEntityState({ calendar_invite_emails: updateInviteEmails })
        setState({ calendarInviteEmail: '' })
      } else {
        const updateProofingEmails = [...new Set([...proofing_emails, email])]
        setEntityState({ proofing_emails: updateProofingEmails })
        setState({ proofingEmail: '' })
      }
    }
  }

  useEffect(() => {
    if (entityState.id === null) setEntityState({ blacklisted_providers: [] })
  }, [])

return (
  <DashboardModule title="Preferences" boxProps={{ marginBottom: 'large' }}>
    <Box flexDirection="column" padding="large">
      <Box flexDirection={['column', 'row']} marginBottom="large">
        <InfiniteLinkingTags
          label="Blacklisted Provider"
          options={filteredProviders}
          selectedItems={linkedBlacklistedProviders}
          onSelect={item => toggleProvider(item, 'add')}
          onRemove={item => toggleProvider(item)}
          onLoadMore={loadNextPage}
          hasMore={hasMore}
          loading={loading}
          placeholder="Search Provider"
          labelKey="name"
          valueKey="id"
          searchFilter={searchFilter}
          onSearchChange={val => setState({ searchFilter: val })}
          showEmptyText={false}
        />
      </Box>
      <Box flexDirection={['column', 'row']} marginBottom="large">
        <FormField
          boxProps={{ flex: 1, paddingBottom: ['large', 0], marginRight: [0, 'medium'] }}
          label="Proofing Emails"
        >
          <Box flexDirection="row" alignItems="center">
            <Box flexDirection="row" width={['100%', 'calc(50% - 4px)']}>
              <input
                name="proofing_emails"
                type="text"
                value={proofingEmail}
                onChange={e => setState({ proofingEmail: e.target.value })}
                onKeyDown={e => (e.key === 'Enter' ? handleAddEmails(e.target.value, 'proofing') : null)}
              />
            </Box>
            <Tag
              boxProps={{
                backgroundColor: '#43ac6a!important',
                fontSize: 'xsmall',
                onClick: () => (proofingEmail ? handleAddEmails(proofingEmail, 'proofing') : null),
                width: 'fit-content',
                marginLeft: 'medium',
              }}
            >
              <Text color="white" fontSize={9} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faPlus} />
              </Text>
            </Tag>
          </Box>

          <Box flexWrap="wrap" gridGap="small" marginTop="medium">
            {proofing_emails?.map(email => (
              <Tag
                boxProps={{
                  width: 'fit-content',
                  fontSize: 'xsmall',
                  onClick: () => setEntityState({
                    proofing_emails: proofing_emails?.filter(x => x !== email),
                  }),
                }}
                key={email}
              >
                <Text color="bodyFontLightColor">
                  {email} &nbsp;<FontAwesomeIcon icon={faTimes} />
                </Text>
              </Tag>
            ))}
          </Box>
        </FormField>
      </Box>
      <Box flexDirection={['column', 'row']} marginBottom="large">
        <FormField
          boxProps={{ flex: 1, paddingBottom: ['large', 0], marginRight: [0, 'medium'] }}
          label="Calendar Invite Emails"
        >
          <Box flexDirection="row" alignItems="center">
            <Box flexDirection="row" width={['100%', 'calc(50% - 4px)']}>
              <input
                name="calendar_invite_emails"
                type="text"
                value={calendarInviteEmail}
                onChange={e => setState({ calendarInviteEmail: e.target.value })}
                onKeyDown={e => (e.key === 'Enter' ? handleAddEmails(e.target.value, 'calendarInvite') : null)}
              />
            </Box>
            <Tag
              boxProps={{
                backgroundColor: '#43ac6a!important',
                fontSize: 'xsmall',
                onClick: () => (calendarInviteEmail ? handleAddEmails(calendarInviteEmail, 'calendarInvite') : null),
                width: 'fit-content',
                marginLeft: 'medium',
              }}
            >
              <Text color="white" fontSize={9} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faPlus} />
              </Text>
            </Tag>
          </Box>

          <Box flexWrap="wrap" gridGap="small" marginTop="medium">
            {calendar_invite_emails?.map(email => (
              <Tag
                boxProps={{
                  width: 'fit-content',
                  fontSize: 'xsmall',
                  onClick: () => setEntityState({
                    calendar_invite_emails: calendar_invite_emails?.filter(x => x !== email),
                  }),
                }}
                key={email}
              >
                <Text color="bodyFontLightColor">
                  {email} &nbsp;<FontAwesomeIcon icon={faTimes} />
                </Text>
              </Tag>
            ))}
          </Box>
        </FormField>
      </Box>
    </Box>
  </DashboardModule>
  )
}

Preferences.propTypes = {
  clientForm: PropTypes.object,
  linkedBlacklistedProviders: PropTypes.array,
  callbacks: PropTypes.object,
}

export default Preferences
